import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChatContext } from '../../context/ChatContext';
import { FiHome, FiUsers, FiBriefcase, FiCalendar, FiClock, FiDollarSign, FiSettings, FiLogOut, FiUserPlus, FiGift, FiList, FiGrid, FiClipboard, FiShare2, FiUser, FiMessageCircle, FiX, FiFileText } from 'react-icons/fi';
import logo from '../../assets/logo.svg';

const staticLinks = [
  { to: '/dashboard', icon: <FiHome />, label: 'Dashboard', roles: ['admin', 'hr', 'employee'] },
  { to: '/employees', icon: <FiUsers />, label: 'Employees', roles: ['admin', 'hr'] },
  { to: '/employees/hierarchy', icon: <FiShare2 />, label: 'Org Hierarchy', roles: ['admin', 'hr'] },
  { to: '/departments', icon: <FiBriefcase />, label: 'Departments', roles: ['admin', 'hr'] },
  { to: '/leaves', icon: <FiCalendar />, label: 'Leaves', roles: ['admin', 'hr', 'employee'] },
  { to: '/leave-balances', icon: <FiList />, label: 'Leave Balances', roles: ['admin', 'hr'] },
  { to: '/holidays', icon: <FiGift />, label: 'Holidays', roles: ['admin', 'hr', 'employee'] },
  { to: '/policies', icon: <FiFileText />, label: 'HR Policies', roles: ['admin', 'hr', 'employee'] },
  { to: '/calendar', icon: <FiGrid />, label: 'My Calendar', roles: ['admin', 'hr', 'employee'] },
  { to: '/projects', icon: <FiClipboard />, label: 'Projects', roles: ['admin', 'hr', 'employee'] },
  { to: '/chat', icon: <FiMessageCircle />, label: 'Chat', roles: ['admin', 'hr', 'employee'] },
  { to: '/attendance', icon: <FiClock />, label: 'Attendance', roles: ['admin', 'hr', 'employee'] },
  { to: '/payroll', icon: <FiDollarSign />, label: 'Payroll', roles: ['admin', 'hr', 'employee'] },
  { to: '/register', icon: <FiUserPlus />, label: 'Register User', roles: ['admin'] },
  { to: '/settings', icon: <FiSettings />, label: 'Settings', roles: ['admin', 'hr', 'employee'] },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { totalUnread } = useChatContext() || {};
  const role = user?.role;

  const links = useMemo(() => {
    const all = [...staticLinks];
    if (role === 'employee' && user?.employeeId) {
      // Insert "My Profile" after Dashboard
      all.splice(1, 0, { to: `/employees/${user.employeeId}`, icon: <FiUser />, label: 'My Profile', roles: ['employee'] });
    }
    return all.filter(link => link.roles.includes(role));
  }, [role, user?.employeeId]);

  return (
    <div className={`sidebar${isOpen ? ' sidebar-mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1 }}>
          <img src={logo} alt="Teleforce" style={{ width: '36px', height: '36px', borderRadius: '8px' }} />
          <div>
            <h2 style={{ margin: 0, lineHeight: 1 }}>Teleforce</h2>
            <p className="sidebar-subtitle" style={{ margin: 0 }}>HRMS</p>
          </div>
        </div>
        <button className="sidebar-close-btn" onClick={onClose} aria-label="Close menu">
          <FiX size={18} />
        </button>
      </div>
      <nav className="sidebar-nav">
        {links.map((link) => (
          <NavLink key={link.to} to={link.to} onClick={onClose} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            {link.icon}
            <span>{link.label}</span>
            {link.to === '/chat' && totalUnread > 0 && (
              <span className="sidebar-chat-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
          <div>
            <div className="sidebar-username">{user?.name}</div>
            <div className="sidebar-role">{user?.role}</div>
          </div>
        </div>
        <button className="sidebar-logout" onClick={logout}><FiLogOut /> Logout</button>
      </div>
    </div>
  );
};

export default Sidebar;
